<template>
  <v-container v-if="selectedUnits != ''" :class="{ 'alerts-tab': showScroll }">
    <FormRow
      label="Enable Speeding Alerts"
      :input-payload="speedingAlerts"
      :check-box="true"
      @parseNewVAl="
        parseNewVal($event, 'speedingAlerts', 'Alerts / Enable Speeding Alerts')
      "
    />
    <br />
    <BorderedContainer label="Accelerometer Alerts">
      <v-row>
        <v-col cols="3" class="align-vertical no-padding">
          <span>Enable: </span>
        </v-col>
        <v-col cols="1" class="align-vertical no-padding">
          <v-checkbox
            v-model="accAlertState"
            item-text="label"
            @change="
              parseNewVal(
                $event,
                'accAlertsEnabled',
                'Alerts / Accelerometer Alerts / Enable'
              )
            "
          />
        </v-col>
        <v-col cols="3" class="align-vertical no-padding">
          <span>Vehicle Type: </span>
        </v-col>
        <v-col cols="4" class="align-vertical no-padding">
          <v-select
            v-model="vehicleType"
            item-text="label"
            :items="vehicleTypeMap[version]"
            @change="
              parseNewVal(
                $event,
                'selectedVehicleType',
                'Alerts / Accelerometer Alerts / Vehicle Type'
              )
            "
          />
        </v-col>
      </v-row>
    </BorderedContainer>
    <br />
    <v-row>
      <v-col cols="1" class="pt-6">Units:</v-col>
      <v-col cols="3" class="pb-6">
        <v-select
          v-model="selectedUnits"
          :items="speedUnitOptions"
          item-text="label"
          item-value="xml"
          @change="
            parseNewVal(
              $event,
              'speedDisplayUnit',
              'Alerts / Accelerometer Alerts / Speed Threshold Unit'
            ),
              convertUnits()
          "
        />
      </v-col>
      <v-col v-if="supportsAdas" cols="3" class="pt-6">
        <span class="unit-label">*For DMS and ADAS</span>
      </v-col>
    </v-row>
    <BorderedContainer
      v-if="includeDMS && displayUpdatedDriverMonitorOptions"
      label="Driver Monitoring Alerts"
    >
      <v-row v-if="isD700 || isJI7A">
        <v-col cols="3" class="align-vertical no-padding">
          <span>Driver Position: </span>
        </v-col>
        <v-col cols="3">
          <v-select
            v-model="selectedDriverSide"
            item-text="label"
            :items="driverSide"
            @change="
              parseNewVal(
                $event.toLowerCase(),
                'setDriverPosition',
                'Alerts / Driver Monitoring / Driver Position'
              )
            "
          />
        </v-col>
      </v-row>

      <div>
        <v-row v-if="!isD700">
          <v-col v-if="!isJI7A" cols="2" class="align-vertical no-padding">
            <span>Camera Channel: </span>
          </v-col>
          <v-col v-if="!isJI7A" cols="1">
            <v-select
              v-model="selectedChannel"
              item-text="label"
              :items="channelCount"
              @change="
                parseNewVal(
                  $event,
                  'selectedChannel',
                  'Alerts / Driver Monitoring / Camera Channel'
                )
              "
            />
          </v-col>
          <v-col v-if="supportsAdas" cols="2" class="align-vertical no-padding">
            <span>Bounding Boxes: </span>
          </v-col>
          <v-col v-if="supportsAdas" cols="2">
            <v-select
              v-if="!isJI7A"
              v-model="selectedDmsBoundingBox"
              :items="boundingBoxOptions"
              @change="setDmsBoundingBox"
            />
            <v-select
              v-else
              v-model="selectedDmsBoundingBox"
              :items="boundingBoxOptionsJi7a"
              @change="setDmsBoundingBox"
            />
          </v-col>
          <!-- <v-col cols="3" class="align-vertical no-padding">
            <span>Region of Interest: </span>
          </v-col>
          <v-col cols="3">
            <v-select
              v-model="selectedRegion"
              item-text="label"
              :items="regions"
              @change="parseNewVal($event.toLowerCase(), 'selectedROI', 'Alerts / Driver Monitoring / Region of Interest')"
            />
          </v-col> -->
        </v-row>

        <v-row>
          <v-col cols="6">
            <div class="setting-container">
              <v-row class="general-height-align">
                <v-col cols="12" class="removing-padding-top">
                  General
                </v-col>
                <v-col v-if="!isD700" cols="12" class="remove-padding-bottom">
                  <DriverMonitoringSystemSettings
                    label="Occlusion Detection"
                    :config="dmsAlertConfigs"
                    :selected-units="selectedUnits"
                    :rate-limiting="false"
                    :display-speed-threshold="false"
                    :display-duration="false"
                    type="occlusion"
                    @dmsAlertsUpdated="dmsEmitWrapper"
                  />
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="3" class="remove-padding-bottom">
                  <span class="dms-heading">Enable Alert</span>
                </v-col>
                <v-col cols="3" class="remove-padding-bottom">
                  <span class="dms-heading"
                    >Speed ({{ castToUpperCase(selectedUnits) }})</span
                  >
                </v-col>
                <v-col cols="3" class="remove-padding-bottom">
                  <span class="dms-heading">Duration (Seconds)</span>
                </v-col>
                <v-col cols="3" class="remove-padding-bottom">
                  <span class="dms-heading">Max Rate (Alerts/Hr)</span>
                </v-col>
              </v-row>

              <DriverMonitoringSystemSettings
                label="Phone"
                :config="dmsAlertConfigs"
                :selected-units="selectedUnits"
                :rate-limiting="true"
                type="phone"
                @dmsAlertsUpdated="dmsEmitWrapper"
              />

              <DriverMonitoringSystemSettings
                label="Eyes Closed"
                :config="dmsAlertConfigs"
                :selected-units="selectedUnits"
                :rate-limiting="true"
                type="fatigue"
                sub-type="eyesClosed"
                @dmsAlertsUpdated="dmsEmitWrapper"
              />

              <DriverMonitoringSystemSettings
                label="Yawning"
                :config="dmsAlertConfigs"
                :selected-units="selectedUnits"
                :rate-limiting="true"
                type="fatigue"
                sub-type="yawn"
                @dmsAlertsUpdated="dmsEmitWrapper"
              />

              <DriverMonitoringSystemSettings
                label="Smoking"
                :config="dmsAlertConfigs"
                :selected-units="selectedUnits"
                :rate-limiting="true"
                type="smoking"
                @dmsAlertsUpdated="dmsEmitWrapper"
              />
            </div>
          </v-col>

          <v-col cols="6">
            <div class="setting-container">
              <v-row>
                <v-col cols="12 removing-padding-top">
                  Distraction
                </v-col>
                <v-col
                  cols="3"
                  class="remove-padding-bottom removing-padding-top"
                  >Max Rate</v-col
                >
                <v-col
                  cols="4"
                  class="remove-padding-bottom removing-padding-top"
                >
                  <v-select
                    v-model="distractionRate"
                    :items="rateLimitBand"
                    label="Alerts/Hr"
                    @change="
                      parseNewVal(
                        $event,
                        'distractionRate',
                        'Alerts / Driver Monitoring / Rate Limit',
                        'distraction'
                      )
                    "
                  />
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="3" class="remove-padding-bottom">
                  <span class="dms-heading">Enable Alert</span>
                </v-col>
                <v-col cols="3" class="remove-padding-bottom">
                  <span class="dms-heading"
                    >Speed ({{ castToUpperCase(selectedUnits) }})</span
                  >
                </v-col>
                <v-col cols="3" class="remove-padding-bottom">
                  <span class="dms-heading">Duration (Seconds)</span>
                </v-col>
                <v-col
                  v-if="dmsAngleSupport"
                  cols="3"
                  class="remove-padding-bottom"
                >
                  <span class="dms-heading">Angle (Degrees)</span>
                </v-col>
              </v-row>

              <DriverMonitoringSystemSettings
                label="Left"
                :config="dmsAlertConfigs"
                :selected-units="selectedUnits"
                :angle-support="dmsAngleSupport"
                type="distraction"
                sub-type="lookLeft"
                @dmsAlertsUpdated="dmsEmitWrapper"
              />

              <DriverMonitoringSystemSettings
                label="Right"
                :config="dmsAlertConfigs"
                :selected-units="selectedUnits"
                :angle-support="dmsAngleSupport"
                type="distraction"
                sub-type="lookRight"
                @dmsAlertsUpdated="dmsEmitWrapper"
              />

              <DriverMonitoringSystemSettings
                label="Down"
                :config="dmsAlertConfigs"
                :selected-units="selectedUnits"
                :angle-support="dmsAngleSupport"
                type="distraction"
                sub-type="lookDown"
                @dmsAlertsUpdated="dmsEmitWrapper"
              />
            </div>
          </v-col>
        </v-row>
      </div>
    </BorderedContainer>
    <br />

    <BorderedContainer
      v-if="includeDMS && !displayUpdatedDriverMonitorOptions"
      label="Driver Monitoring Alerts"
    >
      <div v-if="isD700">
        <v-row>
          <v-col cols="3" class="align-vertical no-padding">
            <span>Driver Position: </span>
          </v-col>
          <v-col cols="3">
            <v-select
              v-model="selectedDriverSide"
              item-text="label"
              :items="driverSide"
              @change="
                parseNewVal(
                  $event.toLowerCase(),
                  'setDriverPosition',
                  'Alerts / Driver Monitoring / Driver Position'
                )
              "
            />
          </v-col>
        </v-row>

        <DMSAlertSettings
          label="Phone Usage Alerts"
          :config="dmsAlertConfigs"
          :rate-limiting="supportsRateLimiting"
          type="phone"
          @dmsAlertsUpdated="dmsEmitWrapper"
        />
        <DMSAlertSettings
          label="Fatigue Alerts"
          :config="dmsAlertConfigs"
          :rate-limiting="supportsRateLimiting"
          type="fatigue"
          @dmsAlertsUpdated="dmsEmitWrapper"
        />
        <DMSAlertSettings
          label="Distraction Alerts"
          :config="dmsAlertConfigs"
          :rate-limiting="supportsRateLimiting"
          type="distraction"
          @dmsAlertsUpdated="dmsEmitWrapper"
        />
        <DMSAlertSettings
          label="Smoking Alerts"
          :config="dmsAlertConfigs"
          :rate-limiting="supportsRateLimiting"
          type="smoking"
          @dmsAlertsUpdated="dmsEmitWrapper"
        />
      </div>

      <div v-if="is8Chn || isNextGen || isJI7A">
        <v-row v-if="!isJI7A">
          <v-col cols="3" class="align-vertical no-padding">
            <span>Camera Channel: </span>
          </v-col>
          <v-col cols="3">
            <v-select
              v-model="selectedChannel"
              item-text="label"
              :items="channelCount"
              @change="
                parseNewVal(
                  $event,
                  'selectedChannel',
                  'Alerts / Driver Monitoring / Camera Channel'
                )
              "
            />
          </v-col>
        </v-row>

        <v-row v-if="!supportsAdas">
          <v-col cols="3" class="align-vertical no-padding">
            <span>Region of Interest: </span>
          </v-col>
          <v-col cols="3">
            <v-select
              v-model="selectedRegion"
              item-text="label"
              :items="regions"
              @change="
                parseNewVal(
                  $event.toLowerCase(),
                  'selectedROI',
                  'Alerts / Driver Monitoring / Region of Interest'
                )
              "
            />
          </v-col>
        </v-row>

        <DMSAlertSettings
          label="Phone Usage Alerts"
          :config="dmsAlertConfigs"
          :selected-units="selectedUnits"
          :rate-limiting="supportsRateLimiting"
          type="phone"
          @dmsAlertsUpdated="dmsEmitWrapper"
        />

        <div v-if="isNextGen || isJI7A">
          <DMSAlertSettings
            label="Fatigue Alerts"
            :config="dmsAlertConfigs"
            :selected-units="selectedUnits"
            :rate-limiting="supportsRateLimiting"
            type="fatigue"
            @dmsAlertsUpdated="dmsEmitWrapper"
          />
          <DMSAlertSettings
            label="Distraction Alerts"
            :config="dmsAlertConfigs"
            :selected-units="selectedUnits"
            :rate-limiting="supportsRateLimiting"
            type="distraction"
            @dmsAlertsUpdated="dmsEmitWrapper"
          />
          <DMSAlertSettings
            label="Smoking Alerts"
            :config="dmsAlertConfigs"
            :selected-units="selectedUnits"
            :rate-limiting="supportsRateLimiting"
            type="smoking"
            @dmsAlertsUpdated="dmsEmitWrapper"
          />
          <DMSAlertSettings
            label="Occlusion Alerts"
            :config="dmsAlertConfigs"
            :selected-units="selectedUnits"
            :rate-limiting="supportsRateLimiting"
            :display-speed-threshold="false"
            type="occlusion"
            @dmsAlertsUpdated="dmsEmitWrapper"
          />
        </div>
      </div>
    </BorderedContainer>

    <br />

    <BorderedContainer v-if="supportsAdas" label="ADAS">
      <v-row>
        <v-col v-if="!isJI7A" cols="2" class="align-vertical no-padding">
          <span>Camera Channel: </span>
        </v-col>
        <v-col v-if="!isJI7A" cols="2">
          <v-select
            v-model="selectedAdasChannel"
            item-text="label"
            :items="channelCount"
            @change="
              parseNewVal(
                $event,
                'selectedAdasChannel',
                'Alerts / ADAS / Camera Channel'
              )
            "
          />
        </v-col>
        <v-col cols="2" class="align-vertical no-padding">
          <span>Bounding Boxes: </span>
        </v-col>
        <v-col cols="2">
          <v-select
            v-if="!isJI7A"
            v-model="selectedAdasBoundingBox"
            :items="boundingBoxOptions"
            @change="setAdasBoundingBox"
          />
          <v-select
            v-else
            v-model="selectedAdasBoundingBox"
            :items="boundingBoxOptionsJi7a"
            @change="setAdasBoundingBox"
          />
        </v-col>
      </v-row>
      <div class="setting-container">
        <v-row>
          <v-col cols="2" class="remove-padding-bottom">
            <span class="dms-heading">Enable Alert</span>
          </v-col>
          <v-col cols="2" class="remove-padding-bottom">
            <span class="dms-heading"
              >Speed ({{ castToUpperCase(selectedUnits) }})</span
            >
          </v-col>
          <v-col cols="2" class="remove-padding-bottom">
            <span class="dms-heading">Max Rate (Alerts/Hr)</span>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="2">
            <v-checkbox
              v-model="adasData.forwardCollision_enabled"
              class="d-flex justify-left"
              label="Forward Collision"
              @change="
                parseNewVal(
                  adasData.forwardCollision_enabled,
                  'adasFwcEnabled',
                  'ADAS / Forward Collision Enabled',
                  'forwardCollision'
                )
              "
            />
          </v-col>

          <v-col cols="2">
            <v-select
              v-model="adasData.forwardCollision_speed"
              :items="speedThresholdList"
              item-text="label"
              item-value="xml"
              @change="
                parseNewValSpeedWrapper(
                  adasData.forwardCollision_speed,
                  'adasFwcSpeed',
                  'ADAS / Forward Collision Speed',
                  'forwardCollision',
                  'forwardCollision_speed'
                )
              "
            />
          </v-col>

          <v-col cols="2">
            <v-select
              v-model="adasData.forwardCollision_rateLimit"
              :items="adasRateLimitBand"
              @change="
                parseNewVal(
                  adasData.forwardCollision_rateLimit,
                  'adasFwcRate',
                  'ADAS / Forward Collision Rate Limit',
                  'forwardCollision'
                )
              "
            />
          </v-col>

          <v-col cols="3" class="pt-6">
            <span>Time to collision (s)</span>
          </v-col>

          <v-col cols="2">
            <v-select
              v-model="adasData.forwardCollision_timeToCollision"
              :items="fcwTimeToCollision"
              @change="
                parseNewVal(
                  adasData.forwardCollision_timeToCollision,
                  'adasFwctimeToCollision',
                  'ADAS / Forward Collision Time to Collision',
                  'forwardCollision'
                )
              "
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="2">
            <v-checkbox
              v-model="adasData.forwardProximity_enabled"
              class="d-flex justify-left"
              label="Tailgating"
              @change="
                parseNewVal(
                  adasData.forwardProximity_enabled,
                  'adasFwpEnabled',
                  'ADAS / Forward Proximity Enabled',
                  'forwardProximity'
                )
              "
            />
          </v-col>

          <v-col cols="2">
            <v-select
              v-model="adasData.forwardProximity_speed"
              :items="speedThresholdList"
              item-text="label"
              item-value="xml"
              @change="
                parseNewValSpeedWrapper(
                  adasData.forwardProximity_speed,
                  'adasFwpSpeed',
                  'ADAS / Forward Proximity Speed',
                  'forwardProximity',
                  'forwardProximity_speed'
                )
              "
            />
          </v-col>

          <v-col cols="2">
            <v-select
              v-model="adasData.forwardProximity_rateLimit"
              :items="adasRateLimitBand"
              @change="
                parseNewVal(
                  adasData.forwardProximity_rateLimit,
                  'adasFwpRate',
                  'ADAS / Forward Proximity Rate Limit',
                  'forwardProximity'
                )
              "
            />
          </v-col>

          <v-col cols="3" class="pt-6">
            <span>Time to collision (s)</span>
          </v-col>

          <v-col cols="2">
            <v-select
              v-model="adasData.forwardProximity_timeToCollision"
              :items="fpwTimeToCollision"
              @change="
                parseNewVal(
                  adasData.forwardProximity_timeToCollision,
                  'adasFwptimeToCollision',
                  'ADAS / Forward Proximity Time to Collision',
                  'forwardProximity'
                )
              "
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="2">
            <v-checkbox
              v-model="adasData.pedestrian_enabled"
              class="d-flex justify-left"
              label="Pedestrian"
              @change="
                parseNewVal(
                  adasData.pedestrian_enabled,
                  'adasPedEnabled',
                  'ADAS / Pedestrian Enabled',
                  'pedestrian'
                )
              "
            />
          </v-col>

          <v-col cols="2">
            <v-select
              v-model="adasData.pedestrian_minSpeed"
              :items="pcwMinThresholdOptions[selectedUnits]"
              item-text="label"
              item-value="xml"
              @change="
                parseNewValSpeedWrapper(
                  adasData.pedestrian_minSpeed,
                  'adasPedMinSpeed',
                  'ADAS / Pedestrian Speed',
                  'pedestrian',
                  'pedestrian_minSpeed'
                )
              "
            />
          </v-col>

          <v-col cols="2">
            <v-select
              v-model="adasData.pedestrian_rateLimit"
              :items="adasRateLimitBand"
              @change="
                parseNewVal(
                  adasData.pedestrian_rateLimit,
                  'adasPedRate',
                  'ADAS / Pedestrian Rate',
                  'pedestrian'
                )
              "
            />
          </v-col>

          <v-col cols="3" class="pt-6">
            <span>Max speed</span>
          </v-col>

          <v-col cols="2">
            <v-select
              v-model="adasData.pedestrian_maxSpeed"
              :items="pcwMaxThresholdOptions[selectedUnits]"
              item-text="label"
              item-value="xml"
              @change="
                parseNewValSpeedWrapper(
                  adasData.pedestrian_maxSpeed,
                  'adasPedMaxSpeed',
                  'ADAS / Pedestrian Max Speed',
                  'pedestrian',
                  'pedestrian_maxSpeed'
                )
              "
            />
          </v-col>
        </v-row>
      </div>

      <br />

      <div class="setting-container">
        <v-row>
          <v-col cols="12" lg="6" sm="12" xs="12">
            <v-img src="@/assets/img/adas_dimensions_1.png" cover />
          </v-col>
          <v-col cols="12" lg="6" sm="12" xs="12" class="pt-5">
            <v-img src="@/assets/img/adas_dimensions_2.png" cover />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="6">
            <span>Camera Position Relative to Centre</span>
          </v-col>
          <v-col cols="6" class="pt-1">
            <v-radio-group v-model="cameraPosition" row>
              <v-radio label="Left" value="left"></v-radio>
              <v-radio label="Right" value="right"></v-radio>
            </v-radio-group>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="6">
            A: Camera to Vehicle Centre ({{ distanceUnits }})
          </v-col>
          <v-col cols="2">
            <v-text-field
              v-model="distanceCameraToCenter"
              type="number"
              @change="
                updateValWrapper(
                  distanceCameraToCenter,
                  'distanceCameraToCenter',
                  'ADAS / Camera Distance'
                )
              "
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="6"> B: Vehicle Width ({{ distanceUnits }}) </v-col>
          <v-col cols="2">
            <v-text-field
              v-model="vehicleWidth"
              type="number"
              @change="
                updateValWrapper(
                  vehicleWidth,
                  'vehicleWidth',
                  'ADAS / Vehicle Width'
                )
              "
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="6"> C: Camera to Ground ({{ distanceUnits }}) </v-col>
          <v-col cols="2">
            <v-text-field
              v-model="distanceCameraToGround"
              type="number"
              @change="
                updateValWrapper(
                  distanceCameraToGround,
                  'distanceCameraToGround',
                  'ADAS / Distance from Ground'
                )
              "
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="6">
            D: Camera to Front Bumper ({{ distanceUnits }})
          </v-col>
          <v-col cols="2">
            <v-text-field
              v-model="distanceCameraToFrontBumper"
              type="number"
              @change="
                updateValWrapper(
                  distanceCameraToFrontBumper,
                  'distanceCameraToFrontBumper',
                  'ADAS / Distance from Front Bumper'
                )
              "
            />
          </v-col>
        </v-row>
      </div>
    </BorderedContainer>
  </v-container>
</template>

<script>
import FormRow from "../components/FormRow.vue";
import BorderedContainer from "../components/BorderedContainer.vue";
import DMSAlertSettings from "../components/DMSAlertSettings.vue";
import DriverMonitoringSystemSettings from "../components/DriverMonitoringSystemSettings.vue";

import { deviceConfigMixin } from "@/mixins/deviceConfigMixin.js";
import { checkIfVersionIsAboveLimit } from "@/utils";

import { Logger } from "aws-amplify";
const logger = new Logger("DMS Alert Configs");

export default {
  name: "Alerts",
  components: {
    FormRow,
    BorderedContainer,
    DMSAlertSettings,
    DriverMonitoringSystemSettings,
  },
  mixins: [deviceConfigMixin],
  props: {
    config: {
      type: Object,
      required: true,
    },
    deviceModel: {
      type: String,
      required: true,
    },
    version: {
      type: String,
      required: true,
    },
    firmwareVersion: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      accAlertState: false,
      vehicleType: "",
      vehicleTypeMap: {
        "1.0.0": [
          {
            xml: 4,
            label: "Car",
          },
          {
            xml: 3,
            label: "Light Van",
          },
          {
            xml: 2,
            label: "Heavy Van",
          },
          {
            xml: 1,
            label: "Small Truck",
          },
          {
            xml: 0,
            label: "Big Truck",
          },
        ],
        "1.0.1": [
          {
            xml: 5,
            label: "Bus/Coach",
          },
          {
            xml: 4,
            label: "Car",
          },
          {
            xml: 3,
            label: "Light Van",
          },
          {
            xml: 2,
            label: "Heavy Van",
          },
          {
            xml: 1,
            label: "Small Truck",
          },
          {
            xml: 0,
            label: "Big Truck",
          },
        ],
        "1.0.2": [
          {
            xml: 5,
            label: "Bus/Coach",
          },
          {
            xml: 4,
            label: "Car",
          },
          {
            xml: 3,
            label: "Light Van",
          },
          {
            xml: 2,
            label: "Heavy Van",
          },
          {
            xml: 1,
            label: "Small Truck",
          },
          {
            xml: 0,
            label: "Big Truck",
          },
        ],
      },
      driverSide: [
        {
          xml: "left",
          label: "Left",
        },
        {
          xml: "right",
          label: "Right",
        },
      ],
      regions: [
        {
          xml: "middle",
          label: "Middle",
        },
        {
          xml: "left",
          label: "Left",
        },
        {
          xml: "right",
          label: "Right",
        },
      ],
      deviceMapRules: {
        "CAMERAMATICS_D700-SD-4G": {
          includeDMS: true,
        },
        "CAMERAMATICS_JH4-HD-4G": {
          includeDMS: false,
        },
        "CAMERAMATICS_JH8-HD-4G": {
          includeDMS: true,
        },
        "CAMERAMATICS_JH4_NT-HD-4G": {
          includeDMS: true,
        },
        "CAMERAMATICS_JH8_NT-HD-4G": {
          includeDMS: true,
        },
        "CAMERAMATICS_JI7A-SD-4G": {
          includeDMS: true,
        },
      },
      selectedDriverSide: "",
      selectedRegion: "",
      selectedChannel: "",
      selectedAdasChannel: "",

      speedUnitOptions: [
        {
          xml: "kph",
          label: "Metric",
        },
        {
          xml: "mph",
          label: "Imperial",
        },
      ],
      selectedUnits: "",
      distractionRate: 0,
      selectedAdasCameraChannel: "",
      adasData: {},

      thresholdOptions: {
        kph: [
          {
            xml: "0",
            label: "0",
          },
          {
            xml: "10",
            label: "10",
          },
          {
            xml: "20",
            label: "20",
          },
          {
            xml: "30",
            label: "30",
          },
          {
            xml: "40",
            label: "40",
          },
          {
            xml: "50",
            label: "50",
          },
          {
            xml: "60",
            label: "60",
          },
          {
            xml: "70",
            label: "70",
          },
        ],
        mph: [
          {
            xml: "0",
            label: "0",
          },
          {
            xml: "10",
            label: "6",
          },
          {
            xml: "20",
            label: "12",
          },
          {
            xml: "30",
            label: "18",
          },
          {
            xml: "40",
            label: "24",
          },
          {
            xml: "50",
            label: "30",
          },
          {
            xml: "60",
            label: "36",
          },
          {
            xml: "70",
            label: "42",
          },
        ],
      },

      pcwMinThresholdOptions: {
        kph: [
          {
            xml: "5",
            label: "5",
          },
          {
            xml: "10",
            label: "10",
          },
          {
            xml: "15",
            label: "15",
          },
          {
            xml: "20",
            label: "20",
          },
          {
            xml: "25",
            label: "25",
          },
        ],
        mph: [
          {
            xml: "5",
            label: "3",
          },
          {
            xml: "10",
            label: "6",
          },
          {
            xml: "15",
            label: "9",
          },
          {
            xml: "20",
            label: "12",
          },
          {
            xml: "25",
            label: "15",
          },
        ],
      },

      pcwMaxThresholdOptions: {
        kph: [
          {
            xml: "10",
            label: "10",
          },
          {
            xml: "15",
            label: "15",
          },
          {
            xml: "20",
            label: "20",
          },
          {
            xml: "25",
            label: "25",
          },
          {
            xml: "30",
            label: "30",
          },
        ],
        mph: [
          {
            xml: "10",
            label: "6",
          },
          {
            xml: "15",
            label: "9",
          },
          {
            xml: "20",
            label: "12",
          },
          {
            xml: "25",
            label: "15",
          },
          {
            xml: "30",
            label: "18",
          },
        ],
      },
      durationOptions: [
        {
          xml: 2,
          label: 2,
        },
        {
          xml: 3,
          label: 3,
        },
        {
          xml: 4,
          label: 4,
        },
        {
          xml: 5,
          label: 5,
        },
        {
          xml: 6,
          label: 6,
        },
        {
          xml: 7,
          label: 7,
        },
        {
          xml: 8,
          label: 8,
        },
      ],
      rateLimit: 0,
      fcwTimeToCollision: [
        "0.5",
        "1.0",
        "1.5",
        "2.0",
        "2.5",
        "3.0",
        "3.5",
        "4.0",
        "4.5",
        "5.0",
        "5.5",
        "6.0",
      ],
      fpwTimeToCollision: ["0.2", "0.4", "0.6", "0.8", "1.0", "1.2"],
      adasSpeedThresholdIndex: {
        forwardCollision_speed: 0,
        forwardProximity_speed: 0,
        laneDeparture_speed: 0,
        pedestrian_maxSpeed: 0,
        pedestrian_minSpeed: 0,
      },
      distanceCameraToCenter: 0,
      distanceCameraToFrontBumper: 0,
      distanceCameraToGround: 0,
      vehicleWidth: 0,
      selectedDmsBoundingBox: "",
      selectedAdasBoundingBox: "",
      boundingBoxOptions: ["None", "On display", "In video", "Both"],
      boundingBoxOptionsJi7a: ["None", "In video"],
      cameraPosition: "",
      loaded: false,
      speedUnitSetCount: 0,
    };
  },
  computed: {
    speedingAlerts() {
      return JSON.parse(this.config.device_config.speeding[0].alertsEnabled[0]);
    },
    selectedVehicleType() {
      return this.config.device_config.peripheral[0].accelerometer[0]
        .thresholdLevel[0];
    },
    accAlertsEnabled() {
      return this.config.device_config.peripheral[0].accelerometer[0]
        .alertsEnabled[0];
    },
    includeDMS() {
      return this.deviceMapRules[this.deviceModel]["includeDMS"];
    },
    dmsAlertConfigs() {
      return this.config.device_config.cv[0].dms[0].alerts[0].alert;
    },
    dmsBoundingBox() {
      return this.config.device_config.cv[0].dms[0].displayBoundingBox[0];
    },
    driverCabPosition() {
      return this.config.device_config.cv[0].dms[0].driverPos[0];
    },
    regionsOfInterest() {
      return this.config.device_config.cv[0].dms[0].roi_pos[0];
    },
    cameraChannel() {
      return this.config.device_config.cv[0].dms[0].channel[0];
    },
    adasCameraChannel() {
      return this.config.device_config.cv[0].adas[0].channel[0];
    },
    adasAlertConfig() {
      return this.config.device_config.cv[0].adas[0].provider[0].alerts[0]
        .alert;
    },
    adasAlertProviderInfo() {
      return this.config.device_config.cv[0].adas[0].provider[0];
    },
    adasInfo() {
      return this.config.device_config.cv[0].adas[0];
    },
    supportsAdas() {
      if (
        !Object.prototype.hasOwnProperty.call(this.config.device_config, "cv")
      ) {
        return false;
      } else {
        return Object.prototype.hasOwnProperty.call(
          this.config.device_config.cv[0],
          "adas"
        ) &&
          this.config.device_config.cv[0].adas[0].selectedProvider[0] == "icar"
          ? true
          : false;
      }
    },
    dmsInfo() {
      return this.config.device_config.cv[0].dms[0];
    },
    channelCount() {
      let length = this.config.device_config.video[0].camera[0].channel.length;
      let items = [];

      for (let i = 1; i <= length; i++) {
        items.push(i);
      }

      return items;
    },
    isD700() {
      return this.deviceModel == "CAMERAMATICS_D700-SD-4G" ? true : false;
    },
    isNextGen() {
      return this.deviceModel == "CAMERAMATICS_JH8_NT-HD-4G" ||
        this.deviceModel == "CAMERAMATICS_JH4_NT-HD-4G"
        ? true
        : false;
    },
    isLegacy8Chn() {
      return this.deviceModel == "CAMERAMATICS_JH8-HD-4G" ? true : false;
    },
    is8Chn() {
      return this.deviceModel == "CAMERAMATICS_JH8-HD-4G" ||
        this.deviceModel == "CAMERAMATICS_JH8_NT-HD-4G"
        ? true
        : false;
    },
    isJI7A() {
      return this.deviceModel == "CAMERAMATICS_JI7A-SD-4G" ? true : false;
    },
    showScroll() {
      return this.deviceModel == "CAMERAMATICS_D700-SD-4G" ||
        this.deviceModel == "CAMERAMATICS_JH4-HD-4G" ||
        this.deviceModel == "CAMERAMATICS_JH8-HD-4G"
        ? false
        : true;
    },
    speedUnits() {
      return this.config.device_config.hasOwnProperty("cv") &&
        this.config.device_config.cv[0].hasOwnProperty("speedDisplayUnit")
        ? this.config.device_config.cv[0].speedDisplayUnit[0]
        : "kph";
    },
    distanceUnits() {
      return this.selectedUnits == "kph" ? "CM" : "In";
    },
    supportsMultipleSpeedUnits() {
      const limit = "01.01.56";

      return checkIfVersionIsAboveLimit(this.firmwareVersion, limit);
    },
    supportsRateLimiting() {
      const map = {
        "CAMERAMATICS_D700-SD-4G": "01.00.98",
        "CAMERAMATICS_JH4_NT-HD-4G": "01.01.81",
        "CAMERAMATICS_JH8_NT-HD-4G": "01.01.81",
        "CAMERAMATICS_JI7A-SD-4G": "01.00.03",
      };
      return map.hasOwnProperty(this.deviceModel)
        ? checkIfVersionIsAboveLimit(
            this.firmwareVersion,
            map[this.deviceModel]
          )
        : false;
    },
    supportsDriverMonitorUpdate() {
      const map = {
        "CAMERAMATICS_D700-SD-4G": "01.01.01",
        "CAMERAMATICS_JH4_NT-HD-4G": "01.01.93",
        "CAMERAMATICS_JH8_NT-HD-4G": "01.01.93",
        "CAMERAMATICS_JI7A-SD-4G": "01.00.30",
      };

      return map.hasOwnProperty(this.deviceModel)
        ? checkIfVersionIsAboveLimit(
            this.firmwareVersion,
            map[this.deviceModel]
          )
        : false;
    },
    supportsUpdatedDriverMonitoringSettings() {
      if (this.deviceModel == "CAMERAMATICS_JI7A-SD-4G") {
        return checkIfVersionIsAboveLimit(this.version, "1.0.0");
      } else {
        return checkIfVersionIsAboveLimit(this.version, "1.0.1");
      }
    },
    displayUpdatedDriverMonitorOptions() {
      return this.supportsDriverMonitorUpdate &&
        this.supportsUpdatedDriverMonitoringSettings
        ? true
        : false;
    },
    dmsAngleSupport() {
      return this.supportsUpdatedDriverMonitoringSettings &&
        this.deviceModel != "CAMERAMATICS_D700-SD-4G"
        ? true
        : false;
    },
    rateLimitBand() {
      let band = [];

      for (let i = 1; i < 31; i++) {
        band.push(i);
      }

      return band;
    },
    adasRateLimitBand() {
      let band = [];

      for (let i = 1; i < 31; i++) {
        band.push(i.toString());
      }

      return band;
    },
    speedThresholdList() {
      return this.thresholdOptions[this.selectedUnits];
    },
  },
  watch: {
    selectedUnits() {
      this.speedUnitSetCount++;
      if (this.speedUnitSetCount > 1) {
        this.convertThresholdSettings();
      }
    },

    cameraPosition() {
      if (this.cameraPosition == "left") {
        let sourceVal = this.distanceCameraToCenter;
        let copyVal = sourceVal;
        let newVal = copyVal * -1 * 10; // we * by 10 because the UI is displaying CM and the API is expecting mm
        let convertedVal =
          this.selectedUnits == "mph" ? this.convertInchesToCm(newVal) : newVal;

        this.parseNewVal(
          convertedVal,
          "distanceCameraToCenter",
          "ADAS / Camera Position to Center"
        );
      } else {
        let sourceVal = this.distanceCameraToCenter;
        let copyVal = sourceVal;
        let newVal = Math.abs(copyVal) * 10; // we * by 10 because the UI is displaying CM and the API is expecting mm
        let convertedVal =
          this.selectedUnits == "mph" ? this.convertInchesToCm(newVal) : newVal;

        this.parseNewVal(
          convertedVal,
          "distanceCameraToCenter",
          "ADAS / Camera Position to Center"
        );
      }
    },
  },
  mounted() {
    this.mapFormProps();

    if (this.supportsAdas) {
      this.mapAdasValues();
      this.setSpeedDistanceIndex();
      this.mapAdasBoundingBox();
    }

    this.mapDmsBoundingBox();
  },
  methods: {
    convertUnits() {
      if (!this.supportsAdas) return;

      if (this.selectedUnits == "mph") {
        this.distanceCameraToCenter = this.convertCmToInches(
          this.distanceCameraToCenter
        );
        this.distanceCameraToFrontBumper = this.convertCmToInches(
          this.distanceCameraToFrontBumper
        );
        this.distanceCameraToGround = this.convertCmToInches(
          this.distanceCameraToGround
        );
        this.vehicleWidth = this.convertCmToInches(this.vehicleWidth);

        this.parseNewVal(
          parseInt(this.convertInchesToCm(this.distanceCameraToCenter)) * 10,
          "distanceCameraToCenter",
          "ADAS / Camera Distance"
        );
        this.parseNewVal(
          parseInt(this.convertInchesToCm(this.distanceCameraToFrontBumper)) *
            10,
          "distanceCameraToFrontBumper",
          "ADAS / Distance from Front Bumper"
        );
        this.parseNewVal(
          parseInt(this.convertInchesToCm(this.distanceCameraToGround)) * 10,
          "distanceCameraToGround",
          "ADAS / Distance from Ground"
        );
        this.parseNewVal(
          parseInt(this.convertInchesToCm(this.vehicleWidth)) * 10,
          "vehicleWidth",
          "ADAS / Vehicle Width"
        );
      } else {
        this.distanceCameraToCenter = this.convertInchesToCm(
          this.distanceCameraToCenter
        );
        this.distanceCameraToFrontBumper = this.convertInchesToCm(
          this.distanceCameraToFrontBumper
        );
        this.distanceCameraToGround = this.convertInchesToCm(
          this.distanceCameraToGround
        );
        this.vehicleWidth = this.convertInchesToCm(this.vehicleWidth);

        this.parseNewVal(
          parseInt(this.distanceCameraToCenter) * 10,
          "distanceCameraToCenter",
          "ADAS / Camera Distance"
        );
        this.parseNewVal(
          parseInt(this.distanceCameraToFrontBumper) * 10,
          "distanceCameraToFrontBumper",
          "ADAS / Distance from Front Bumper"
        );
        this.parseNewVal(
          parseInt(this.distanceCameraToGround) * 10,
          "distanceCameraToGround",
          "ADAS / Distance from Ground"
        );
        this.parseNewVal(
          parseInt(this.vehicleWidth) * 10,
          "vehicleWidth",
          "ADAS / Vehicle Width"
        );
      }
    },

    updateValWrapper(value, target, label) {
      let sourceVal = value;
      let copyVal = sourceVal;
      let newVal = copyVal;

      if (this.selectedUnits == "mph") {
        newVal = this.convertInchesToCm(newVal) * 10;
      } else {
        newVal = newVal * 10;
      }

      if (target == "distanceCameraToCenter" && this.cameraPosition == "left") {
        newVal = newVal * -1;
      }

      this.parseNewVal(newVal, `${target}`, `${label}`);
    },

    setAdasBoundingBox() {
      if (this.selectedAdasBoundingBox == "None") {
        if (!this.isJI7A) {
          this.parseNewVal(false, "adasDisplayBox", "ADAS / Bounding box");
        }
        this.parseNewVal(false, "adasRecordBox", "ADAS / Bounding box");
      }

      if (this.selectedAdasBoundingBox == "On display") {
        this.parseNewVal(true, "adasDisplayBox", "ADAS / Bounding box");
        this.parseNewVal(false, "adasRecordBox", "ADAS / Bounding box");
      }

      if (this.selectedAdasBoundingBox == "In video") {
        if (!this.isJI7A) {
          this.parseNewVal(false, "adasDisplayBox", "ADAS / Bounding box");
        }
        this.parseNewVal(true, "adasRecordBox", "ADAS / Bounding box");
      }

      if (this.selectedAdasBoundingBox == "Both") {
        this.parseNewVal(true, "adasDisplayBox", "ADAS / Bounding box");
        this.parseNewVal(true, "adasRecordBox", "ADAS / Bounding box");
      }
    },

    setDmsBoundingBox() {
      if (this.selectedDmsBoundingBox == "None") {
        if (!this.isJI7A) {
          this.parseNewVal(false, "dmsDisplayBox", "DMS / Bounding box");
        }
        this.parseNewVal(false, "dmsRecordBox", "DMS / Bounding box");
      }

      if (this.selectedDmsBoundingBox == "On display") {
        this.parseNewVal(true, "dmsDisplayBox", "DMS / Bounding box");
        this.parseNewVal(false, "dmsRecordBox", "DMS / Bounding box");
      }

      if (this.selectedDmsBoundingBox == "In video") {
        if (!this.isJI7A) {
          this.parseNewVal(false, "dmsDisplayBox", "DMS / Bounding box");
        }
        this.parseNewVal(true, "dmsRecordBox", "DMS / Bounding box");
      }

      if (this.selectedDmsBoundingBox == "Both") {
        this.parseNewVal(true, "dmsDisplayBox", "DMS / Bounding box");
        this.parseNewVal(true, "dmsRecordBox", "DMS / Bounding box");
      }
    },

    mapAdasBoundingBox() {
      const display = !this.isJI7A
        ? JSON.parse(this.adasInfo.displayBoundingBox[0])
        : false;
      const record = JSON.parse(this.adasInfo.recordBoundingBox[0]);
      logger.info("mapAdasBoundingBox", display, record);
      if (!display && !record) {
        this.selectedAdasBoundingBox = "None";
      }

      if (display && !record) {
        this.selectedAdasBoundingBox = "On display";
      }

      if (!display && record) {
        this.selectedAdasBoundingBox = "In video";
      }

      if (display && record) {
        this.selectedAdasBoundingBox = "Both";
      }
    },

    mapDmsBoundingBox() {
      const display = !this.isJI7A
        ? JSON.parse(this.dmsInfo.displayBoundingBox[0])
        : false;
      const record = JSON.parse(this.dmsInfo.recordBoundingBox[0]);

      if (!display && !record) {
        this.selectedDmsBoundingBox = "None";
      }

      if (display && !record) {
        this.selectedDmsBoundingBox = "On display";
      }

      if (!display && record) {
        this.selectedDmsBoundingBox = "In video";
      }

      if (display && record) {
        this.selectedDmsBoundingBox = "Both";
      }
    },

    setSpeedDistanceIndex(type = null) {
      for (var prop in this.adasSpeedThresholdIndex) {
        if (
          Object.prototype.hasOwnProperty.call(
            this.adasSpeedThresholdIndex,
            prop
          )
        ) {
          if (prop == "pedestrian_maxSpeed") {
            this.adasSpeedThresholdIndex[prop] = this.pcwMaxThresholdOptions[
              this.selectedUnits
            ].findIndex((item) => {
              return parseInt(item.xml) == parseInt(this.adasData[prop]);
            });
          } else if (prop == "pedestrian_minSpeed") {
            this.adasSpeedThresholdIndex[prop] = this.pcwMinThresholdOptions[
              this.selectedUnits
            ].findIndex((item) => {
              return parseInt(item.xml) == parseInt(this.adasData[prop]);
            });
          } else {
            this.adasSpeedThresholdIndex[prop] = this.thresholdOptions[
              this.selectedUnits
            ].findIndex((item) => {
              return parseInt(item.xml) == parseInt(this.adasData[prop]);
            });
          }
        }
      }

      if (typeof type == "string" && type.length > 0) {
        if (type == "pedestrian_maxSpeed") {
          this.adasSpeedThresholdIndex[prop] = this.pcwMaxThresholdOptions[
            this.selectedUnits
          ].findIndex((item) => {
            return parseInt(item.xml) == parseInt(this.adasData[prop]);
          });
        } else if (type == "pedestrian_minSpeed") {
          this.adasSpeedThresholdIndex[prop] = this.pcwMinThresholdOptions[
            this.selectedUnits
          ].findIndex((item) => {
            return parseInt(item.xml) == parseInt(this.adasData[prop]);
          });
        } else {
          this.adasSpeedThresholdIndex[prop] = this.thresholdOptions[
            this.selectedUnits
          ].findIndex((item) => {
            return parseInt(item.xml) == parseInt(this.adasData[prop]);
          });
        }
      }
    },

    convertThresholdSettings() {
      const typeMap = {
        forwardCollision_speed: "forwardCollision",
        forwardProximity_speed: "forwardProximity",
        pedestrian_maxSpeed: "pedestrian",
        pedestrian_minSpeed: "pedestrian",
      };
      const labelMap = {
        forwardCollision_speed: "ADAS / Forward Collision Speed",
        forwardProximity_speed: "ADAS / Forward Proximity Speed",
        pedestrian_maxSpeed: "ADAS / Pedestrian Max Speed",
        pedestrian_minSpeed: "ADAS / Pedestrian Speed",
      };

      const keyMap = {
        forwardCollision_speed: "adasFwcSpeed",
        forwardProximity_speed: "adasFwpSpeed",
        pedestrian_maxSpeed: "adasPedMaxSpeed",
        pedestrian_minSpeed: "adasPedMinSpeed",
      };

      // only set the ADAS values if the device supports ADAS.
      if (this.supportsAdas) {
        for (var prop in this.adasSpeedThresholdIndex) {
          if (
            Object.prototype.hasOwnProperty.call(
              this.adasSpeedThresholdIndex,
              prop
            )
          ) {
            let index = this.adasSpeedThresholdIndex[prop];

            if (index > -1) {
              let valueList = [];

              if (prop == "pedestrian_maxSpeed") {
                valueList = this.pcwMaxThresholdOptions;
              } else if (prop == "pedestrian_minSpeed") {
                valueList = this.pcwMinThresholdOptions;
              } else {
                valueList = this.thresholdOptions;
              }
              logger.info("convertThresholdSettings ", prop, valueList, index);
              let value = valueList[this.selectedUnits][index].xml;
              this.adasData[prop] = value;

              if (keyMap[prop]) {
                this.parseNewVal(
                  value,
                  keyMap[prop],
                  labelMap[prop],
                  typeMap[prop]
                );
              }

              logger.info(
                "speed index",
                value,
                keyMap[prop],
                labelMap[prop],
                typeMap[prop]
              );
            }
          }
        }
      }
    },

    convertInchesToCm(val) {
      return Math.round(val * 2.54);
    },
    convertCmToInches(val) {
      return Math.round(val / 2.54);
    },

    mapAdasValues() {
      this.adasAlertConfig.forEach((item) => {
        const type = item.type[0];

        Object.keys(item).forEach((key) => {
          logger.info("adas mapping", key);
          if (key !== "type") {
            const propName = `${type}_${key}`;

            let value = item[key][0]; // get string value

            // Convert "true"/"false" to actual booleans
            if (value === "true" || value === "false") {
              value = JSON.parse(value);
            }

            this.adasData[propName] = value;
          }
        });
      });

      if (this.selectedUnits == "mph") {
        this.distanceCameraToCenter = this.convertCmToInches(
          parseInt(this.adasAlertProviderInfo.distanceCameraToCenter[0]) / 10
        );
        this.distanceCameraToFrontBumper = this.convertCmToInches(
          parseInt(this.adasAlertProviderInfo.distanceCameraToFrontBumper[0]) /
            10
        );
        this.distanceCameraToGround = this.convertCmToInches(
          parseInt(this.adasAlertProviderInfo.distanceCameraToGround[0]) / 10
        );
        this.vehicleWidth = this.convertCmToInches(
          parseInt(this.adasAlertProviderInfo.vehicleWidth[0]) / 10
        );
      } else {
        this.distanceCameraToCenter =
          parseInt(this.adasAlertProviderInfo.distanceCameraToCenter[0]) / 10;
        this.distanceCameraToFrontBumper =
          parseInt(this.adasAlertProviderInfo.distanceCameraToFrontBumper[0]) /
          10;
        this.distanceCameraToGround =
          parseInt(this.adasAlertProviderInfo.distanceCameraToGround[0]) / 10;
        this.vehicleWidth =
          parseInt(this.adasAlertProviderInfo.vehicleWidth[0]) / 10;
      }
      logger.info(
        "mapAdasValues",
        this.adasAlertProviderInfo,
        this.vehicleWidth,
        this.distanceCameraToGround,
        this.distanceCameraToFrontBumper
      );

      this.selectedAdasChannel = !this.isJI7A
        ? parseInt(this.adasInfo.channel[0])
        : "";

      if (this.distanceCameraToCenter < 0) {
        this.cameraPosition = "left";

        this.distanceCameraToCenter = Math.abs(this.distanceCameraToCenter);
      } else {
        this.cameraPosition = "right";
      }
    },
    castToUpperCase(str) {
      if (!str || str == "") return;
      return str.toUpperCase();
    },
    mapFormProps() {
      logger.info("mapFormProps called");
      this.selectedUnits = this.speedUnits;
      this.accAlertState = JSON.parse(this.accAlertsEnabled);
      this.vehicleType = this.vehicleTypeMap[this.version].find((option) => {
        return option.xml === parseInt(this.selectedVehicleType);
      });

      if (this.isD700 || this.isJI7A) {
        let obj = this.driverSide.find((side) => {
          return side.xml == this.driverCabPosition;
        });

        this.selectedDriverSide = obj.label;
      }

      if (this.is8Chn || this.isNextGen) {
        this.selectedChannel = parseInt(this.cameraChannel);
      }

      if (this.is8Chn || this.isNextGen || this.isJI7A) {
        if (this.supportsAdas) return;
        this.selectedRegion = this.regions.find((region) => {
          return region.xml == this.regionsOfInterest;
        });
      }

      if (this.supportsUpdatedDriverMonitoringSettings) {
        const distractionSettings = this.dmsAlertConfigs.find(
          (config) => config.type[0] == "distraction"
        );
        logger.info("distraction settings ", distractionSettings);
        if (distractionSettings) {
          logger.info("distraction settings pls", distractionSettings);
          this.distractionRate = parseInt(distractionSettings.rateLimit[0]);
        }
      }

      this.loaded = true;
    },

    parseNewValSpeedWrapper(val, id, label, context, type) {
      this.setSpeedDistanceIndex(type);
      this.parseNewVal(val, id, label, context);
    },

    parseNewVal(val, id, label, context, parent = false) {
      let payload = {};
      logger.info("alerts parseNewVal parent", val, id, label, context, parent);
      if (context) {
        payload = {
          newValue: this.returnMappedVal(val, [
            this.vehicleTypeMap[this.version],
          ]),
          key: this.configPropMapper(id),
          label: label,
          context: {
            newValue: context,
            key: this.configPropMapper(context),
            label: label,
          },
        };
        if (parent) {
          payload["parent"] = {
            newValue: parent,
            key: this.configPropMapper(parent),
            label: label,
          };
        }
      } else {
        payload = {
          newValue: this.returnMappedVal(val, [
            this.vehicleTypeMap[this.version],
          ]),
          key: this.configPropMapper(id),
          label: label,
        };
      }

      if (id == "selectedVehicleType") {
        this.$emit(
          "settingsUpdated",
          payload,
          this.vehicleTypeMap[this.version]
        );
      } else {
        this.$emit("settingsUpdated", payload);
      }
    },
    configPropMapper(key) {
      let map = {
        speedingAlerts: "device_config.speeding[0].alertsEnabled[0]",
        selectedVehicleType:
          "device_config.peripheral[0].accelerometer[0].thresholdLevel[0]",
        speedDisplayUnit: "device_config.cv[0].speedDisplayUnit[0]",
        accAlertsEnabled:
          "device_config.peripheral[0].accelerometer[0].alertsEnabled[0]",
        dmsPhoneAlerts:
          "device_config.cv[0].dms[0].alerts[0].alert[0].enabled[0]",
        dmsSmokingAlerts:
          "device_config.cv[0].dms[0].alerts[0].alert[3].enabled[0]",
        dmsFatigueAlerts:
          "device_config.cv[0].dms[0].alerts[0].alert[1].enabled[0]",
        dmsDistractionAlerts:
          "device_config.cv[0].dms[0].alerts[0].alert[2].enabled[0]",
        dmsOcclusionAlerts:
          "device_config.cv[0].dms[0].alerts[0].alert[4].enabled[0]",
        dmsPhoneSpeed: "device_config.cv[0].dms[0].alerts[0].alert[0].speed[0]",
        dmsSmokingSpeed:
          "device_config.cv[0].dms[0].alerts[0].alert[3].speed[0]",
        dmsFatigueSpeed:
          "device_config.cv[0].dms[0].alerts[0].alert[1].speed[0]",
        dmsDistractionSpeed:
          "device_config.cv[0].dms[0].alerts[0].alert[2].speed[0]",
        setDriverPosition: "device_config.cv[0].dms[0].driverPos[0]",
        selectedChannel: "device_config.cv[0].dms[0].channel[0]",
        selectedROI: "device_config.cv[0].dms[0].roi_pos[0]",
        phone: "device_config.cv[0].dms[0].alerts[0].alert[0].type[0]",
        fatigue: "device_config.cv[0].dms[0].alerts[0].alert[1].type[0]",
        distraction: "device_config.cv[0].dms[0].alerts[0].alert[2].type[0]",
        smoking: "device_config.cv[0].dms[0].alerts[0].alert[3].type[0]",
        occlusion: "device_config.cv[0].dms[0].alerts[0].alert[4].type[0]",
        eyesClosed:
          "device_config.cv[0].dms[0].alerts[0].alert[1].event[0].type[0]",
        yawn: "device_config.cv[0].dms[0].alerts[0].alert[1].event[1].type[0]",
        lookLeft:
          "device_config.cv[0].dms[0].alerts[0].alert[2].event[0].type[0]",
        lookRight:
          "device_config.cv[0].dms[0].alerts[0].alert[2].event[1].type[0]",
        lookDown:
          "device_config.cv[0].dms[0].alerts[0].alert[2].event[2].type[0]",
        dmsPhoneRateLimit:
          "device_config.cv[0].dms[0].alerts[0].alert[0].rateLimit[0]",
        dmsSmokingRateLimit:
          "device_config.cv[0].dms[0].alerts[0].alert[3].rateLimit[0]",
        dmsFatigueRateLimit:
          "device_config.cv[0].dms[0].alerts[0].alert[1].rateLimit[0]",
        dmsDistractionRateLimit:
          "device_config.cv[0].dms[0].alerts[0].alert[2].rateLimit[0]",
        dmsOcclusionRateLimit:
          "device_config.cv[0].dms[0].alerts[0].alert[0].alert[4].rateLimit[0]",
        dmsYawnRateLimit:
          "device_config.cv[0].dms[0].alerts[0].alert[1].event[1].rateLimit[0]",
        dmsEyesClosedRateLimit:
          "device_config.cv[0].dms[0].alerts[0].alert[1].event[0].rateLimit[0]",
        dmsPhoneDuration:
          "device_config.cv[0].dms[0].alerts[0].alert[0].duration[0]",
        dmsEyesClosedDuration:
          "device_config.cv[0].dms[0].alerts[0].alert[1].event[0].duration[0]",
        dmsYawnDuration:
          "device_config.cv[0].dms[0].alerts[0].alert[1].event[1].duration[0]",
        dmsSmokingDuration:
          "device_config.cv[0].dms[0].alerts[0].alert[3].duration[0]",
        dmsLookLeftDuration:
          "device_config.cv[0].dms[0].alerts[0].alert[2].event[0].duration[0]",
        dmsLookRightDuration:
          "device_config.cv[0].dms[0].alerts[0].alert[2].event[1].duration[0]",
        dmsLookDownDuration:
          "device_config.cv[0].dms[0].alerts[0].alert[2].event[2].duration[0]",
        dmsEyesClosed:
          "device_config.cv[0].dms[0].alerts[0].alert[1].event[0].enabled[0]",
        dmsYawn:
          "device_config.cv[0].dms[0].alerts[0].alert[1].event[1].enabled[0]",
        dmsLookLeft:
          "device_config.cv[0].dms[0].alerts[0].alert[2].event[0].enabled[0]",
        dmsLookRight:
          "device_config.cv[0].dms[0].alerts[0].alert[2].event[1].enabled[0]",
        dmsLookDown:
          "device_config.cv[0].dms[0].alerts[0].alert[2].event[2].enabled[0]",
        dmsLookLeftSpeed:
          "device_config.cv[0].dms[0].alerts[0].alert[2].event[0].speed[0]",
        dmsLookRightSpeed:
          "device_config.cv[0].dms[0].alerts[0].alert[2].event[1].speed[0]",
        dmsLookDownSpeed:
          "device_config.cv[0].dms[0].alerts[0].alert[2].event[2].speed[0]",
        dmsEyesClosedSpeed:
          "device_config.cv[0].dms[0].alerts[0].alert[1].event[0].speed[0]",
        dmsYawnSpeed:
          "device_config.cv[0].dms[0].alerts[0].alert[1].event[1].speed[0]",
        distractionRate:
          "device_config.cv[0].dms[0].alerts[0].alert[2].rateLimit[0]",
        dmsLookLeftAngle:
          "device_config.cv[0].dms[0].alerts[0].alert[2].event[0].angle[0]",
        dmsLookRightAngle:
          "device_config.cv[0].dms[0].alerts[0].alert[2].event[1].angle[0]",
        dmsLookDownAngle:
          "device_config.cv[0].dms[0].alerts[0].alert[2].event[2].angle[0]",

        adasDisplayBox: "device_config.cv[0].adas[0].displayBoundingBox[0]",
        adasRecordBox: "device_config.cv[0].adas[0].recordBoundingBox[0]",

        dmsDisplayBox: "device_config.cv[0].dms[0].displayBoundingBox[0]",
        dmsRecordBox: "device_config.cv[0].dms[0].recordBoundingBox[0]",

        adasLdEnabled:
          "device_config.cv[0].adas[0].provider[0].alerts[0].alert[0].enabled[0]",
        adasLdDistance:
          "device_config.cv[0].adas[0].provider[0].alerts[0].alert[0].distance[0]",
        adasLdRate:
          "device_config.cv[0].adas[0].provider[0].alerts[0].alert[0].rateLimit[0]",
        adasLdSpeed:
          "device_config.cv[0].adas[0].provider[0].alerts[0].alert[0].speed[0]",
        laneDeparture:
          "device_config.cv[0].adas[0].provider[0].alerts[0].alert[0].type[0]",

        adasFwcEnabled:
          "device_config.cv[0].adas[0].provider[0].alerts[0].alert[1].enabled[0]",
        adasFwctimeToCollision:
          "device_config.cv[0].adas[0].provider[0].alerts[0].alert[1].timeToCollision[0]",
        adasFwcRate:
          "device_config.cv[0].adas[0].provider[0].alerts[0].alert[1].rateLimit[0]",
        adasFwcSpeed:
          "device_config.cv[0].adas[0].provider[0].alerts[0].alert[1].speed[0]",
        forwardCollision:
          "device_config.cv[0].adas[0].provider[0].alerts[0].alert[1].type[0]",

        adasFwpEnabled:
          "device_config.cv[0].adas[0].provider[0].alerts[0].alert[2].enabled[0]",
        adasFwptimeToCollision:
          "device_config.cv[0].adas[0].provider[0].alerts[0].alert[2].timeToCollision[0]",
        adasFwpRate:
          "device_config.cv[0].adas[0].provider[0].alerts[0].alert[2].rateLimit[0]",
        adasFwpSpeed:
          "device_config.cv[0].adas[0].provider[0].alerts[0].alert[2].speed[0]",
        forwardProximity:
          "device_config.cv[0].adas[0].provider[0].alerts[0].alert[2].type[0]",

        adasPedEnabled:
          "device_config.cv[0].adas[0].provider[0].alerts[0].alert[3].enabled[0]",
        adasPedtimeToCollision:
          "device_config.cv[0].adas[0].provider[0].alerts[0].alert[3].timeToCollision[0]",
        adasPedRate:
          "device_config.cv[0].adas[0].provider[0].alerts[0].alert[3].rateLimit[0]",
        adasPedMinSpeed:
          "device_config.cv[0].adas[0].provider[0].alerts[0].alert[3].minSpeed[0]",
        adasPedMaxSpeed:
          "device_config.cv[0].adas[0].provider[0].alerts[0].alert[3].maxSpeed[0]",
        pedestrian:
          "device_config.cv[0].adas[0].provider[0].alerts[0].alert[3].type[0]",

        distanceCameraToCenter:
          "device_config.cv[0].adas[0].provider[0].distanceCameraToCenter[0]",
        distanceCameraToFrontBumper:
          "device_config.cv[0].adas[0].provider[0].distanceCameraToFrontBumper",
        distanceCameraToGround:
          "device_config.cv[0].adas[0].provider[0].distanceCameraToGround",
        vehicleWidth: "device_config.cv[0].adas[0].provider[0].vehicleWidth",

        selectedAdasChannel: "device_config.cv[0].adas[0].channel[0]",
      };

      return map[key];
    },
    dmsEmitWrapper(data, id, context, label, parent) {
      logger.info("dmsEmitWrapper ", data, id, context, label);
      this.parseNewVal(
        data,
        id,
        `Alerts / Driver Monitoring / ${label}`,
        context,
        parent
      );
    },
    checkIfVersionIsUpdated(oldVer, newVer) {
      const oldParts = oldVer.split(".");
      const newParts = newVer.split(".");

      for (var i = 0; i < newParts.length; i++) {
        const a = ~~newParts[i];
        const b = ~~oldParts[i];

        if (a > b) return true;
        if (a < b) return false;
      }

      return false;
    },
  },
};
</script>

<style lang="scss">
.align-vertical {
  justify-content: left;
  align-items: center;
  display: flex;
}
.no-padding {
  padding: 0 !important;
  margin: 0 !important;
}

.remove-padding-bottom {
  padding-bottom: 0;
}

.removing-padding-top {
  padding-top: 0;
}

.dms-heading {
  font-size: 15px;
}

.general-height-align {
  padding-bottom: 14px;
}

.setting-container {
  background: #80808026;
  padding: 0 12px;
}

.v-text-field__details {
  display: none !important;
}

.alerts-tab {
  height: 575px;
  overflow: scroll;
}
</style>
